import React, { useEffect, Suspense } from 'react';
import {BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';

// Composants existants
import Header from './components/Header';
import Footer from './components/Footer';
import ElectionsLegislativesPage from "./pages/LeglislativesPage";
import ActualitesPage from "./pages/ActualitesPage";

// Page d'accueil avec toutes les sections
import HeroSection from './components/HeroSection';
import PresidentMessage from './components/PresidentMessage';
import VisionSection from './components/VisionSection';
import LogoExplanationSection from "./components/LogoExplanationSection";
// Import du nouveau composant d'adhésion intégré
import AdhesionSectionIntegrated from './components/AdhesionSectionIntegrated';
import MemberPage from './pages/MemberPage';
import OrganigrammePage from "./pages/OrganigrammePage";

// Pages séparées
import FederationsPage from './pages/FederationPage';
import AdhesionPage from './pages/AdhesionPage';
import VisionPage from './pages/VisionPage';

// NOUVEAU IMPORT - Page Commissariat Politique
import CommissariatPolitiquePage from './pages/CommissariatPolitiquePage';

// IMPORTS WORDPRESS
import { ArticlePage } from './components/News/ArticlePage';

// NOUVEAUX IMPORTS ADMIN
import { useAdminAuth } from './hooks/useAdminAuth';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorFallback from './components/common/ErrorFallback';
import AdminLayout from './components/layout/AdminLayout';
import ProtectedRoute from './components/admin/ProtectedRoute';
import FacebookSection from "./components/FacebookSection";
import ElectionsLocalesPage from "./pages/LocalesPage";


// Pages admin - Lazy loading pour optimiser les performances
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/DashboardPage'));
const MemberManagementPage = React.lazy(() => import('./pages/admin/MemberManagementPage'));
const MemberDetailsPage = React.lazy(() => import('./pages/admin/MemberDetailsPage'));

// Pages admin - Enquêtes et Formations
const AdminSurveysPage = React.lazy(() => import('./pages/admin/AdminPage').then(module => ({ default: module.AdminSurveysPage })));
const AdminFormationsPage = React.lazy(() => import('./pages/admin/AdminPage').then(module => ({ default: module.AdminFormationsPage })));

// 🆕 NOUVELLE PAGE - Completion adhésion papier
const CompletePaperPage = React.lazy(() => import('./pages/CompletePaperPage'));

// 🆕 NOUVELLES PAGES SECTIONS MEMBRES (à router quand prêt)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage'));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SurveysPage = React.lazy(() => import('./pages/SurveysPage'));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const VolunteerPage = React.lazy(() => import('./pages/VolunteerPage'));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FormationPage = React.lazy(() => import('./pages/FormationPage'));


// Composant de chargement pour les pages admin
const AdminPageLoading: React.FC = () => (
    <div className="flex items-center justify-center h-64">
        <div className="text-center">
            <LoadingSpinner size="md" />
            <p className="mt-2 text-gray-600">Chargement de la page...</p>
        </div>
    </div>
);

// Composant ScrollToTop (inchangé)
const ScrollToTop: React.FC = () => {
    const { pathname, hash } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Si on arrive avec un hash depuis une autre page
        if (hash && pathname === '/') {
            // Attendre que la page se charge complètement
            setTimeout(() => {
                const element = document.querySelector(hash);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        } else if (!hash) {
            // Scroll normal en haut de page
            window.scrollTo(0, 0);
        }
    }, [pathname, hash]);

    // Rediriger vers la page d'accueil si on essaie d'accéder à une ancre depuis une autre page
    useEffect(() => {
        if (hash && pathname !== '/' && /^#[a-zA-Z0-9_-]+$/.test(hash)) {
            navigate(`/`);
        }
    }, [pathname, hash, navigate]);

    return null;
};

// Composant pour la page d'accueil
const HomePage: React.FC = () => {
    useEffect(() => {
        // Smooth scroll behavior for anchor links
        const handleAnchorClick = (e: Event) => {
            const target = e.target as HTMLAnchorElement;
            if (target.href && target.href.includes('#')) {
                e.preventDefault();
                const id = target.href.split('#')[1];
                const element = document.getElementById(id);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        };

        document.addEventListener('click', handleAnchorClick);

        // Intersection Observer for animations
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in');
                }
            });
        }, observerOptions);

        const sections = document.querySelectorAll('section');
        sections.forEach((section) => observer.observe(section));

        return () => {
            document.removeEventListener('click', handleAnchorClick);
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <HeroSection />
            <FacebookSection/>
            <PresidentMessage />
            <LogoExplanationSection />
            <VisionSection />

            <AdhesionSectionIntegrated />
        </>
    );
};

// Composant principal de routage
const AppRoutes: React.FC = () => {
    const { user } = useAdminAuth();

    return (
        <Routes>
            {/* Page d'accueil avec section actualités */}
            <Route path="/" element={<HomePage />} />

            {/* Pages existantes */}
            <Route path="/vision" element={<VisionPage />} />
            <Route path="/federations" element={<FederationsPage />} />
            <Route path="/adhesion" element={<AdhesionPage />} />
            <Route path="/membre" element={<MemberPage />} />

            {/* 🆕 NOUVELLE ROUTE - Completion adhésion papier */}
            <Route 
                path="/complete-paper" 
                element={
                    <Suspense fallback={<AdminPageLoading />}>
                        <CompletePaperPage />
                    </Suspense>
                } 
            />

            {/* NOUVELLE ROUTE - Commissariat Politique */}
            <Route path="/commissariat-politique" element={<CommissariatPolitiquePage />} />
            <Route path="/organigramme" element={<OrganigrammePage />} />

            <Route path="/legislatives2025" element={<ElectionsLegislativesPage />} />

            <Route path="/locales2025" element={<ElectionsLocalesPage />} />

            {/* 🆕 ROUTES SECTIONS MEMBRE - Toutes gérées par MemberPage */}
            <Route path="/membre/*" element={<MemberPage />} />
            <Route
                path="/membre/adhesion"
                element={<Navigate to="/adhesion" replace />}
            />

            {/* ROUTES WORDPRESS */}
            {/* Liste complète des actualités */}
            <Route path="/actualites" element={<ActualitesPage />} />
            {/* Article individuel par slug */}
            <Route path="/actualites/:slug" element={<ArticlePage />} />

            {/* NOUVELLES ROUTES ADMIN */}
            {/* Route de connexion admin */}
            <Route
                path="/admin/login"
                element={
                    user ? (
                        <Navigate to="/admin/dashboard" replace />
                    ) : (
                        <Suspense fallback={<AdminPageLoading />}>
                            <LoginPage />
                        </Suspense>
                    )
                }
            />

            {/* Routes d'administration protégées */}
            <Route
                path="/admin/*"
                element={
                    <ProtectedRoute>
                        <AdminLayout>
                            <Suspense fallback={<AdminPageLoading />}>
                                <Routes>
                                    {/* Dashboard admin */}
                                    <Route
                                        path="dashboard"
                                        element={
                                            <ProtectedRoute requiredPermission="read">
                                                <AdminDashboardPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Gestion des membres */}
                                    <Route
                                        path="members"
                                        element={
                                            <ProtectedRoute requiredPermission="read">
                                                <MemberManagementPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Détails d'un membre */}
                                    <Route
                                        path="members/:id"
                                        element={
                                            <ProtectedRoute requiredPermission="read">
                                                <MemberDetailsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Gestion des enquêtes */}
                                    <Route
                                        path="surveys"
                                        element={
                                            <ProtectedRoute requiredPermission="write">
                                                <AdminSurveysPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Gestion des formations */}
                                    <Route
                                        path="formations"
                                        element={
                                            <ProtectedRoute requiredPermission="write">
                                                <AdminFormationsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Redirection par défaut vers le dashboard */}
                                    <Route
                                        path=""
                                        element={<Navigate to="dashboard" replace />}
                                    />

                                    {/* Page non trouvée pour les routes admin */}
                                    <Route path="*" element={<NotFoundAdminPage />} />
                                </Routes>
                            </Suspense>
                        </AdminLayout>
                    </ProtectedRoute>
                }
            />

            {/* Redirection pour /admin sans slash */}
            <Route
                path="/admin"
                element={
                    user ? (
                        <Navigate to="/admin/dashboard" replace />
                    ) : (
                        <Navigate to="/admin/login" replace />
                    )
                }
            />

            {/* Page 404 pour les routes non trouvées */}
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

// Composant principal App avec gestion d'erreurs
const App: React.FC = () => {
    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, errorInfo) => {
                console.error('Application Error:', error, errorInfo);
            }}
        >
            <HelmetProvider>
                <Router>
                    <ScrollToTop />

                    {/* Toast notifications pour l'admin */}
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                            success: {
                                duration: 3000,
                                iconTheme: {
                                    primary: '#10B981',
                                    secondary: '#fff',
                                },
                            },
                            error: {
                                duration: 5000,
                                iconTheme: {
                                    primary: '#EF4444',
                                    secondary: '#fff',
                                },
                            },
                        }}
                    />

                    <div className="App">
                        {/* Header conditionnel - ne pas afficher sur les pages admin */}
                        <ConditionalHeader />

                        <AppRoutes />

                        {/* Footer conditionnel - ne pas afficher sur les pages admin */}
                        <ConditionalFooter />

                        {/* Loading indicator (global) */}
                        <div
                            id="loading-indicator"
                            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 hidden"
                        >
                            <div className="spinner"></div>
                        </div>
                    </div>
                </Router>
            </HelmetProvider>
        </ErrorBoundary>
    );
};

// Composant pour afficher le header conditionnellement
const ConditionalHeader: React.FC = () => {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');

    if (isAdminRoute) return null;
    return <Header />;
};

// Composant pour afficher le footer conditionnellement
const ConditionalFooter: React.FC = () => {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const isMemberRoute = location.pathname.startsWith('/membre');

    console.log('🦶 Footer check:', { pathname: location.pathname, isAdminRoute, isMemberRoute });

    if (isAdminRoute || isMemberRoute) {
        console.log('🚫 Footer masqué pour:', location.pathname);
        return null;
    }
    console.log('✅ Footer affiché pour:', location.pathname);
    return <Footer />;
};


// Composant 404 pour les pages admin
const NotFoundAdminPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Page d'administration non trouvée
                </h2>
                <p className="text-gray-600 mb-8">
                    Cette page d'administration n'existe pas.
                </p>
                <a
                    href="/admin/dashboard"
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Retour au dashboard
                </a>
            </div>
        </div>
    );
};

// Composant 404 personnalisé AVEC le lien vers le commissariat
const NotFoundPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="text-center">
                <div className="mb-8">
                    <h1 className="text-9xl font-bold text-gray-300 mb-4">404</h1>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Page non trouvée
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-md mx-auto">
                        Désolé, la page que vous recherchez n'existe pas ou a été déplacée.
                    </p>
                </div>

                <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="/"
                            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            Retour à l'accueil
                        </a>

                        <a
                            href="/actualites"
                            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                            </svg>
                            Voir les actualités
                        </a>
                    </div>

                    {/* Liens rapides AVEC les nouvelles sections */}
                    <div className="mt-8 pt-8 border-t border-gray-200">
                        <p className="text-gray-600 mb-4">Ou explorez ces sections :</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                            <a href="/vision" className="text-blue-600 hover:text-blue-800 underline">Notre Vision</a>
                            <span className="text-gray-400">•</span>
                            <a href="/commissariat-politique" className="text-blue-600 hover:text-blue-800 underline">Commissariat Politique</a>
                            <span className="text-gray-400">•</span>
                            <a href="/federations" className="text-blue-600 hover:text-blue-800 underline">Fédérations</a>
                            <span className="text-gray-400">•</span>
                            <a href="/adhesion" className="text-blue-600 hover:text-blue-800 underline">Adhésion</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default App;
